import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AppBar, Box, Collapse, Drawer, IconButton, List, ListItemButton, ListItemText, Toolbar } from "@mui/material";
import { motion, useAnimation } from "framer-motion";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext';
import { PowerBiLoginRequest } from "../AuthConfig";
import { MenuItem } from "../models/models";
import { useUnifiedAuthentication } from "../components/CommonComponents";
import { isRunningInTeams, LoginUser } from '../services/AppOwnsDataWebApi'
import { app, authentication } from "@microsoft/teams-js";

interface MenuItemProps {
    name: string;
    link?: string;
    level: number;
    children?: MenuItemProps[];
}

interface MenuProps {
    items: MenuItemProps[];
}

const HeaderMobile = ({ items }: MenuProps) => {
    const isAuthenticated = useUnifiedAuthentication();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
    const rootItems: MenuItem[] = [];
    const { instance } = useMsal();
    const { embeddingData, setDisplayMainLoader, isSuperUser, setIsTeamsAuthenticated, isTeamsAuthenticated, setTeamsUser } = useContext(AppContext);
    const [drawerWidth, setDrawerWidth] = useState('40%');

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const toggleExpand = (name: string) => {
        setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));
    };
    const line1Animation = useAnimation();
    const line2Animation = useAnimation();
    const line3Animation = useAnimation();
    const menuIconRef = useRef(null);

    const animateLines = async (hovered: boolean) => {
        await line1Animation.start({ width: hovered ? '30px' : '30px', transition: { duration: 0.1 } });
        await line2Animation.start({ width: hovered ? '30px' : '30px', transition: { duration: 0.15 } });
        await line3Animation.start({ width: hovered ? '30px' : '25px', transition: { duration: 0.175 } });
    };

    const logoutUser = () => {
        setMenuOpen(false);
        setDisplayMainLoader(false);
        embeddingData.setRedirectUserToReport(false);
        navigate("/");
        if (isTeamsAuthenticated === true)
            setIsTeamsAuthenticated(false);

        if (isTeamsAuthenticated === false)
            instance.logoutRedirect();
    };

    const loginUser = async () => {
        const inTeams = await isRunningInTeams();
        if (inTeams) {
            try {
                const context = await app.getContext();
                const token = await authentication.getAuthToken({ silent: true });
                await LoginUser(context.user?.userPrincipalName, context.user?.displayName);
                setTeamsUser({
                    userPrincipalName: context.user?.userPrincipalName,
                    displayName: context.user?.userPrincipalName,
                });
                setIsTeamsAuthenticated(true);
                navigate("/");
            }
            catch (error) {
                console.error("Teams SSO failed", error);
            }
        }
        else
            instance.loginRedirect(PowerBiLoginRequest);
    };

    useEffect(() => {
        animateLines(false);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const windowWidth = window.innerWidth;
            let newWidth = '60%';

            if (windowWidth > 1200) {
                newWidth = '40%';
            } else if (windowWidth > 800) {
                newWidth = '50%';
            }

            setDrawerWidth(newWidth);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const permissionBasedObjects: MenuItem[] = [
        {
            id: -1000,
            name: 'SETTINGS',
            level: 0,
            parentId: null,
            link: null,
            displayOrder: 1000,
            isAssigned: true
        },
        {
            id: -1001,
            name: 'MANAGEMENT',
            level: 1,
            parentId: -1000,
            link: null,
            displayOrder: 1000,
            isAssigned: true
        },
        {
            id: -1002,
            name: 'USER',
            level: 2,
            parentId: -1001,
            link: '/usermanagement',
            displayOrder: 0,
            isAssigned: true
        },
        {
            id: -1003,
            name: 'CONTENT',
            level: 2,
            parentId: -1001,
            link: '/contentmanagement',
            isAssigned: true,
            displayOrder: 3
        },
        {
            id: -1004,
            name: 'PACKAGE',
            level: 2,
            parentId: -1001,
            link: '/content-role-management',
            isAssigned: true,
            displayOrder: 2
        },
        {
            id: -1006,
            name: 'MONITOR',
            level: 2,
            parentId: -1001,
            link: '/monitormanagement',
            isAssigned: true,
            displayOrder: 4
        },
        {
            id: -1005,
            name: process.env.REACT_APP_VERSION,
            level: 0,
            parentId: null,
            link: null,
            displayOrder: 1000,
            isAssigned: true
        }
    ];


    if (isAuthenticated && isSuperUser) {
        const menuMap: { [id: number]: MenuItem } = {};

        permissionBasedObjects.forEach(item => {
            menuMap[item.id] = { ...item, children: [] };
        });

        permissionBasedObjects.forEach(item => {
            if (item.parentId != null && menuMap[item.parentId]) {
                menuMap[item.parentId].children!.push(menuMap[item.id]);
            } else {
                rootItems.push(menuMap[item.id]);
            }
        });

        const sortItems = (items: MenuItem[]) => {
            items.sort((a, b) => a.displayOrder - b.displayOrder);
            items.forEach(item => {
                if (item.children && item.children.length > 0) {
                    sortItems(item.children);
                }
            });
        };
    }

    const renderMenuItems = (items: MenuItemProps[]) => {
        return items.map((item: MenuItemProps) => (
            <div
                key={item.name}>
                <ListItemButton
                    onClick={(event: any) => {
                        event.preventDefault();
                        event.stopPropagation();
                        navigate(item.link)
                    }}
                    component="a"
                    href={item.children?.length ? undefined : item.link}
                    sx={{
                        paddingTop: '0px',
                        paddingBottom: '0px',
                        paddingLeft: '40px'
                    }}
                >
                    <ListItemText
                        primary={item.name}
                        sx={{
                            fontWeight: "700 !important",
                            textAlign: "left",
                            color: "white",
                            textTransform: "uppercase",
                            fontFamily: "'Barlow', sans-serif !important",
                        }}
                    />
                    {item.children?.length ? (
                        expanded[item.name] ? (
                            <ExpandLessIcon
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    toggleExpand(item.name);
                                }}
                                sx={{ color: "white" }}
                            />
                        ) : (
                            <ExpandMoreIcon
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    toggleExpand(item.name);
                                }}
                                sx={{ color: "white" }}
                            />
                        )
                    ) : null}

                </ListItemButton>
                {item.children?.length ? (
                    <Collapse
                        in={expanded[item.name]}
                        timeout="auto"
                        unmountOnExit>
                        <List sx={{ pl: 2 }}>{renderMenuItems(item.children)}</List>
                    </Collapse>
                ) : null}
            </div>

        ));
    };

    return (
        <AppBar position="fixed"
            sx={{
                backgroundColor: isAuthenticated ? "#00594d" : 'transparent',
                boxShadow: 'none',
                borderBottom: isAuthenticated ? '2px solid white' : '',
                height: "auto",
                minHeight: "60px",
            }}>
            <Toolbar
                sx={{
                    display: "flex",
                    justifyContent: "space-between"
                }}>
                <Box>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 228 24" fill="none" preserveAspectRatio="xMidYMid meet" aria-label="Travelheart Family"
                        style={{ maxHeight: '50px', height: '32px', width: 'auto', opacity: 1, zIndex: 4 }}>
                        <g fill="#81EC97">
                            <path d="m16.96 7.83-2.265 1.351s-2.234-.713-2.421-.781c-.304-.11-.36-.162-.36-.36a.36.36 0 0 1 .156-.297c.059-.04.734-.58.734-.58s4.894.018 4.918 0c.07-.058.114-.097.158-.136l1.5-1.27c.557-.556 1.294-.723 1.645-.373.35.35.184 1.086-.374 1.643l-.986.962-.56.551-.46 4.998s-.654.61-.7.664a.36.36 0 0 1-.311.124c-.198-.02-.242-.082-.32-.395a292 292 0 0 1-.526-2.488l1.84-2.446-5.458 5.285S9.314 17.87 5.143 17.26c1.73 2.017 4.053 4.201 7.15 6.459.228.131.506.235.788.235.319 0 .601-.096.871-.276C24.966 15.651 26.146 8.51 26.191 6.151 26.188 2.761 23.138.01 19.377.01c-2.844 0-5.26 1.597-6.277 3.835C12.092 1.598 9.645.01 6.786.01 3.024.01-.025 2.76-.025 6.15v.058c.034 1.194.364 3.589 2.088 6.705 0 0 6.453 3.62 14.896-5.084zM220.893 19.226h1.519q.186 0 .229-.208l.729-5.904q0-.083.021-.105l4.537-8.066q.042-.104 0-.177-.043-.072-.146-.072h-1.645q-.166 0-.249.166l-3.123 5.905q-.021.042-.062.041-.04 0-.062-.041l-1.686-5.905q-.063-.166-.208-.166h-1.505q-.208 0-.208.186l.021.063 2.378 8.066q.022.022.022.105l-.729 5.904q-.042.208.167.208m-10.006.021h7.555q.186 0 .229-.208l.167-1.31q0-.207-.187-.207h-5.723q-.084 0-.084-.083l1.54-12.537q.042-.207-.167-.207h-1.54q-.186 0-.229.207l-1.727 14.137q-.043.208.167.208m-5.475 0h1.54q.187 0 .229-.208l1.728-14.137q.041-.207-.167-.207h-1.54q-.188 0-.23.207l-1.727 14.137q-.043.208.167.208M201.501 4.84l-3.393 4.699q-.022.02-.063.02t-.042-.02l-2.248-4.699q-.103-.145-.229-.145h-1.54q-.187 0-.229.207L192.03 19.04q-.042.208.167.208h1.54q.186 0 .229-.208l1.27-10.436q.02-.062.052-.073.031-.01.052.052l1.936 4.054q.084.145.229.145h.312q.126 0 .271-.145l2.893-4.096q.041-.062.083-.053.041.01.021.073l-1.29 10.478a.16.16 0 0 0 .031.146q.051.062.156.062h1.52a.23.23 0 0 0 .166-.062.2.2 0 0 0 .063-.146l1.748-14.137q0-.207-.187-.207h-1.519a.32.32 0 0 0-.27.145m-17.849 10.084 2.788-7.526q.021-.042.042-.042t.042.042l.937 7.526q.02.02-.01.052a.1.1 0 0 1-.073.032h-3.663q-.042 0-.062-.032-.021-.03 0-.052m4.536 4.324h1.603a.22.22 0 0 0 .146-.052.17.17 0 0 0 .062-.135v-.042l-2.019-14.137q-.042-.186-.208-.186h-1.894q-.166 0-.249.186l-5.474 14.137q-.042.105.01.167t.156.062h1.561q.187 0 .25-.187l.874-2.412a.14.14 0 0 1 .125-.062h4.454q.084 0 .105.062l.291 2.412q.042.187.208.187M181.96 6.212l.167-1.31q.04-.207-.167-.207h-7.66q-.187 0-.229.207l-1.727 14.137q-.042.208.167.208h1.54q.187 0 .229-.208l.749-6.153q0-.041.031-.063a.13.13 0 0 1 .073-.02h3.83q.188 0 .228-.209l.146-1.309q.041-.208-.167-.208h-3.83q-.083 0-.084-.083l.562-4.49q0-.084.084-.084h5.849a.2.2 0 0 0 .145-.062.2.2 0 0 0 .063-.146M48.616 4.824a.24.24 0 0 1 .072.177v2.468q0 .104-.072.177a.24.24 0 0 1-.177.072h-3.114q-.105 0-.104.104v11.2q0 .103-.072.176a.24.24 0 0 1-.177.072h-2.949a.24.24 0 0 1-.177-.072.24.24 0 0 1-.072-.177v-11.2q0-.103-.104-.103h-2.99a.24.24 0 0 1-.176-.072.24.24 0 0 1-.073-.177V5.001q0-.104.073-.177a.24.24 0 0 1 .177-.072h9.758q.104 0 .177.072M56.946 19.062l-1.952-5.496q-.042-.083-.103-.083h-.83q-.105 0-.104.104v5.433q0 .104-.072.177a.24.24 0 0 1-.177.072h-2.949a.24.24 0 0 1-.177-.072.24.24 0 0 1-.072-.177V5q0-.104.072-.177a.24.24 0 0 1 .177-.072h5.315q1.288 0 2.274.57t1.526 1.587.54 2.344c0 .884-.187 1.631-.561 2.281a3.63 3.63 0 0 1-1.557 1.472q-.084.02-.041.125l2.262 5.848a.2.2 0 0 1 .022.103q0 .187-.229.187h-3.094q-.208 0-.27-.208zm-2.99-11.261v2.945q0 .104.103.103h1.454q.665 0 1.07-.415.405-.414.405-1.14c0-.484-.135-.889-.405-1.172q-.404-.425-1.07-.425H54.06q-.105 0-.104.104M69.597 19.042l-.415-1.825q-.02-.083-.103-.084h-3.51q-.083 0-.103.084l-.416 1.825q-.041.229-.27.228h-3.052q-.27 0-.208-.27L65.236 4.96q.062-.207.29-.208h3.634q.229 0 .29.208L73.168 19l.021.084q0 .186-.228.186h-3.093q-.23 0-.27-.228m-3.405-4.563h2.222a.14.14 0 0 0 .084-.03q.04-.033.02-.094L67.357 9.21q-.023-.083-.063-.083t-.062.083l-1.142 5.165q-.021.104.103.103M76.658 19.062 73.087 5.021v-.083q0-.187.228-.187h3.198q.227 0 .27.228l1.784 8.856q.022.061.063.062.042 0 .062-.062l1.744-8.856q.021-.228.27-.228h3.115q.291 0 .228.269L80.436 19.06q-.061.208-.29.208h-3.198q-.23 0-.29-.208zM94.958 7.625a.24.24 0 0 1-.177.072h-5.357q-.105 0-.104.104v2.592q0 .104.104.104h3.239q.103 0 .177.072a.24.24 0 0 1 .072.177v2.447q0 .104-.072.177a.24.24 0 0 1-.177.072h-3.24q-.103 0-.103.103v2.676q0 .103.104.103h5.357q.103 0 .177.072a.24.24 0 0 1 .072.177v2.447q0 .104-.072.177a.24.24 0 0 1-.177.072h-8.659a.24.24 0 0 1-.177-.072.24.24 0 0 1-.072-.177V5q0-.104.072-.177a.24.24 0 0 1 .177-.072h8.659q.103 0 .177.072A.24.24 0 0 1 95.03 5v2.447q0 .104-.072.177M96.96 19.198a.24.24 0 0 1-.073-.177V5.001q0-.104.072-.177a.24.24 0 0 1 .177-.072h2.949a.245.245 0 0 1 .249.249v11.22q0 .105.104.104h5.439q.104 0 .177.072a.24.24 0 0 1 .073.177v2.447q0 .104-.073.177a.24.24 0 0 1-.177.072h-8.74a.24.24 0 0 1-.178-.072M113.64 4.824a.24.24 0 0 1 .177-.072h2.949q.103 0 .176.072a.24.24 0 0 1 .073.177v14.02a.24.24 0 0 1-.073.177.24.24 0 0 1-.176.072h-2.949a.24.24 0 0 1-.177-.072.24.24 0 0 1-.072-.177v-5.476q0-.103-.104-.103h-2.678q-.103 0-.103.103v5.476q0 .104-.073.177a.24.24 0 0 1-.177.072h-2.948a.24.24 0 0 1-.177-.072.24.24 0 0 1-.073-.177V5.001q0-.104.073-.177a.24.24 0 0 1 .177-.072h2.948q.104 0 .177.072a.24.24 0 0 1 .073.177v5.392q0 .104.103.104h2.678q.105 0 .104-.104V5.001q0-.104.072-.177M128.706 7.625a.24.24 0 0 1-.177.072h-5.357q-.103 0-.103.104v2.592q0 .104.103.104h3.239q.104 0 .177.072a.24.24 0 0 1 .073.177v2.447q0 .104-.073.177a.24.24 0 0 1-.177.072h-3.239q-.103 0-.103.103v2.676q0 .103.103.103h5.357q.104 0 .177.072a.24.24 0 0 1 .073.177v2.447a.24.24 0 0 1-.073.177.24.24 0 0 1-.177.072h-8.658a.24.24 0 0 1-.177-.072.24.24 0 0 1-.072-.177V5q0-.104.072-.177a.24.24 0 0 1 .177-.072h8.658q.104 0 .177.072a.24.24 0 0 1 .073.177v2.447a.24.24 0 0 1-.073.177M137.916 19.042l-.415-1.825q-.021-.083-.104-.084h-3.509q-.084 0-.104.084l-.415 1.825q-.042.229-.27.228h-3.052q-.27 0-.208-.27l3.717-14.041q.063-.207.29-.208h3.635q.228 0 .29.208L141.488 19l.021.084q0 .186-.228.186h-3.094q-.229 0-.27-.228zm-3.405-4.563h2.222q.04 0 .083-.03.04-.033.021-.094l-1.163-5.144q-.021-.083-.062-.083t-.063.083l-1.142 5.165q-.02.104.104.103M149.737 19.062l-1.951-5.496q-.042-.083-.104-.083h-.83q-.104 0-.104.104v5.433q0 .104-.072.177a.24.24 0 0 1-.177.072h-2.948a.24.24 0 0 1-.177-.072.24.24 0 0 1-.073-.177V5a.24.24 0 0 1 .073-.177.24.24 0 0 1 .177-.072h5.314q1.288 0 2.274.57.987.57 1.526 1.587.54 1.017.54 2.344c0 .884-.187 1.631-.561 2.281a3.63 3.63 0 0 1-1.557 1.472q-.083.02-.041.125l2.263 5.848a.2.2 0 0 1 .021.103q0 .187-.228.187h-3.093q-.208 0-.271-.208zm-2.99-11.261v2.945q0 .104.104.103h1.454q.664 0 1.069-.415.405-.414.405-1.14c0-.484-.134-.889-.405-1.172q-.405-.425-1.069-.425h-1.454q-.104 0-.104.104M164.539 4.824a.24.24 0 0 1 .072.177v2.468q0 .104-.072.177a.24.24 0 0 1-.177.072h-3.114q-.105 0-.104.104v11.2q0 .103-.072.176a.24.24 0 0 1-.177.072h-2.949a.245.245 0 0 1-.249-.249v-11.2q0-.103-.104-.103h-2.989a.24.24 0 0 1-.177-.072.24.24 0 0 1-.073-.177V5.001a.24.24 0 0 1 .073-.177.24.24 0 0 1 .177-.072h9.758q.103 0 .177.072" />
                        </g>
                    </svg>
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    onMouseEnter={() => {
                        animateLines(true);
                        toggleMenu();
                    }}
                    onMouseLeave={() => {
                        animateLines(false);
                    }}
                    ref={menuIconRef}
                >
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <motion.div animate={line1Animation} style={{ height: '3px', backgroundColor: 'white', marginBottom: '4px' }} />
                        <motion.div animate={line2Animation} style={{ height: '3px', backgroundColor: 'white', marginBottom: '4px' }} />
                        <motion.div animate={line3Animation} style={{ height: '3px', marginRight: 'auto', backgroundColor: 'white' }} />
                    </Box>
                </IconButton>
            </Toolbar>
            <Drawer
                anchor="right"
                open={menuOpen}
                onClose={toggleMenu}
                style={{ color: 'white' }}
                PaperProps={{
                    sx: { backgroundColor: "#00594d", width: drawerWidth },
                    onMouseLeave: toggleMenu
                }}
            >
                <motion.div
                    initial={{ x: "100%" }}
                    animate={{ x: menuOpen ? 0 : "100%" }}
                    transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                    <IconButton
                        edge="end"
                        color="inherit"
                        sx={{
                            float: 'right',
                            marginRight: '20px',
                            marginTop: '20px',
                        }}
                    >
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <motion.div animate={line1Animation} style={{ height: '3px', backgroundColor: 'white', marginBottom: '4px' }} />
                            <motion.div animate={line2Animation} style={{ height: '3px', backgroundColor: 'white', marginBottom: '4px' }} />
                            <motion.div animate={line3Animation} style={{ height: '3px', marginRight: 'auto', backgroundColor: 'white' }} />
                        </Box>
                    </IconButton>
                    <List sx={{ marginTop: '100px' }}>
                        {isAuthenticated && items && items.length > 0 ? renderMenuItems([...items]) : <ListItemText primary="" />}
                        {isAuthenticated ? renderMenuItems([...rootItems]) : <ListItemText primary="" />}
                        {isAuthenticated ? (
                            <div
                                key={"Logout"}>
                                <ListItemButton
                                    onClick={() => { logoutUser(); }}
                                    component="a"
                                    sx={{
                                        paddingTop: '0px',
                                        paddingBottom: '0px',
                                        paddingLeft: '40px'
                                    }}
                                >
                                    <ListItemText
                                        primary={"Logout"}
                                        sx={{
                                            fontWeight: "700 !important",
                                            textAlign: "left",
                                            color: "white",
                                            textTransform: "uppercase",
                                            fontFamily: "'Barlow', sans-serif !important",
                                        }}
                                    />
                                </ListItemButton>
                            </div>) : (
                            <div
                                key={"LogIn"}>
                                <ListItemButton
                                    onClick={() => { loginUser(); }}
                                    component="a"
                                    sx={{
                                        paddingTop: '0px',
                                        paddingBottom: '0px',
                                        paddingLeft: '40px'
                                    }}
                                >
                                    <ListItemText
                                        primary={"LogIn"}
                                        sx={{
                                            fontWeight: "700",
                                            textAlign: "left",
                                            color: "white",
                                            textTransform: "uppercase",
                                            fontFamily: "'Barlow', sans-serif !important",
                                        }}
                                    />
                                </ListItemButton>
                            </div>
                        )}
                    </List>
                </motion.div>
            </Drawer>
        </AppBar>
    );
};

export default HeaderMobile;

